/**
 * The Facebook storm-ad landing page: one template, ten versions picked by
 * `?v=<slug>`. The copy lives in storm-versions.json so it can be edited
 * without touching code; this module only decides which version a URL gets.
 */

import copy from "./storm-versions.json";

export interface StormVersion {
  slug: string;
  /** The ad code the version's ad carries as utm_content; null for the default. */
  code: string | null;
  hook: string;
  /** The dream outcome that leads the sign-up box; empty keeps the plain box. */
  outcome: string;
  /** Awaiting Roger's approval: shown only on the review link, never to ad visitors. */
  draftOutcome: string;
  storm: string[];
  /** "Inside the guide: principles …"; empty hides it. Shown only with the guide. */
  principles: string;
}

export type LeadMagnetSlot = typeof copy.leadMagnet;

/** What the offer card shows, after the optional lead magnet is folded in. */
export interface StormOffer {
  eyebrow: string;
  title: string;
  /** Under an outcome, in place of the eyebrow and title: what the 30 days are. */
  withOutcome: string;
  /** Empty hides it. */
  subtitle: string;
  /** What the 30 days include, listed above the email field. */
  points: string[];
  /** Empty hides it. */
  body: string;
  button: string;
  busy: string;
  /** Right under the button: what removes the risk of saying yes. */
  assurance: string;
  finePrint: string;
  thanksTitle: string;
  thanksBody: string;
  /** The proof line under the button, the same on every version. */
  proof: string;
  /** Only when a lead magnet is configured. */
  delivery: string | null;
  /** Only when a lead magnet is configured and has a cover image. */
  cover: string | null;
  /** Only when a lead magnet is configured: the thank-you download. */
  download: { label: string; path: string } | null;
}

/**
 * The lead magnet is an optional slot in storm-versions.json, and its `path`
 * is the one place the download's URL lives. A file in apps/site/public/files/
 * under an unguessable name, so the magnet is not a URL people pass around
 * instead of signing up. That is not access control: anyone holding the link
 * can download it without an email. public/_headers keeps /files/* out of
 * search. Replace a file at the same path so no link already sent out breaks.
 *
 * Empty title or path means no magnet: the page offers the free 30 days alone
 * and shows no guide, cover, principles line or download anywhere.
 */
export function resolveOffer(
  slot: LeadMagnetSlot = copy.leadMagnet,
  base: typeof copy.offer = copy.offer
): StormOffer {
  const filled = slot.title.trim() !== "" && slot.path.trim() !== "";
  if (!filled) {
    return { ...base, proof: copy.proof, delivery: null, cover: null, download: null };
  }

  return {
    ...base,
    proof: copy.proof,
    eyebrow: slot.eyebrow || base.eyebrow,
    title: slot.title,
    subtitle: slot.subtitle || base.subtitle,
    withOutcome: slot.withOutcome || base.withOutcome,
    body: slot.body || base.body,
    button: slot.button || base.button,
    thanksBody: slot.thanksBody || base.thanksBody,
    delivery: slot.delivery || null,
    // ?? because an older copy file (or a test's) may not have the field yet.
    cover: (slot.cover ?? "").trim() || null,
    download: { label: slot.download || "Download", path: slot.path },
  };
}

export const STORM_OFFER: StormOffer = resolveOffer();
/** Under Roger's photo at the top of every version. */
export const STORM_BYLINE: string = copy.byline;
export const STORM_VERSIONS: StormVersion[] = copy.versions;
export const DEFAULT_VERSION: StormVersion = copy.default;

/**
 * Not copy, so not in the JSON: compliance requires these exact words on the
 * page, and an edit to the draft copy must not be able to drop or reword them.
 */
export const STORM_DISCLAIMER =
  "Educational research for all readers. Not personal investment advice. Past performance does not guarantee future results. The author may own securities discussed.";

/**
 * An unknown, missing or oddly cased `v` falls back to the neutral default —
 * never an error. A mistyped ad URL still lands on a working page, and the
 * stored landing_variant says "default", which is how the typo gets noticed.
 */
export function resolveStormVersion(search: string): StormVersion {
  const v = new URLSearchParams(search).get("v")?.trim().toLowerCase();
  return STORM_VERSIONS.find((version) => version.slug === v) ?? DEFAULT_VERSION;
}

/**
 * Roger reviews draft copy on the live page by adding `review=1` to a /go
 * link. That link is not a secret, only unadvertised: the page is noindex and
 * no ad points at it. Ad visitors never see a draft.
 */
export function isReviewMode(search: string): boolean {
  return new URLSearchParams(search).get("review") === "1";
}

/** The outcome a visitor sees: the approved one, or on the review link the draft. */
export function resolveOutcome(version: StormVersion, review: boolean): string {
  return (review && version.draftOutcome.trim()) || version.outcome.trim();
}

/**
 * The versions either side of this one, wrapping, for the review bar's
 * buttons. The default page steps into the first version.
 */
export function reviewNeighbors(version: StormVersion): {
  position: number;
  previous: StormVersion;
  next: StormVersion;
} {
  const i = STORM_VERSIONS.findIndex((v) => v.slug === version.slug);
  const n = STORM_VERSIONS.length;
  if (i < 0) return { position: 0, previous: STORM_VERSIONS[n - 1], next: STORM_VERSIONS[0] };
  return {
    position: i + 1,
    previous: STORM_VERSIONS[(i - 1 + n) % n],
    next: STORM_VERSIONS[(i + 1) % n],
  };
}
